import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { FiEdit2, FiEye } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { toast } from "react-toastify";

import SearchFilter from "../../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../../Common/ColumnVisibilityControl";
import ActionButton from "../../../../Common/ActionButton";
import StatusChip from "../../../../Common/StatusChip";
import DeleteConfirmModal from "../../../../../Common/DeleteConfirmModal";
import { formatAmountWithCommas } from "../../../../../Utils/utils";
import QuoteDetailsModal from "./QuoteDetailsModal";
import { getQuotes, getQuoteById, deleteQuote, quoteChipType } from "./quotesService";

/**
 * Quotations List Table
 * Route: /procurement/quotations/quotes
 */
const QuotesTable = () => {
  const history = useHistory();

  const gridRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnVisibility, setColumnVisibility] = useState({});
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [viewQuote, setViewQuote] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load quotes from local storage / service
  const loadQuotesData = useCallback(() => {
    getQuotes()
      .then(setQuotes)
      .catch((err) => toast.error(err.message || "Failed to load quotations"));
  }, []);

  useEffect(() => {
    loadQuotesData();
  }, [loadQuotesData]);

  // Client-side quick filter
  const filteredQuotes = useMemo(() => {
    if (!searchTerm) {
      return quotes;
    }
    const lower = searchTerm.toLowerCase();
    return quotes.filter((item) =>
      [
        item?.QuoteNo || "",
        item?.VendorName || "",
        item?.ProjectName || "",
        item?.RequisitionNo || "",
        item?.PaymentTerms || "",
        item?.Status || "",
        item?.QuoteDate || "",
        item?.ValidTill || "",
        String(item?.GrandTotal || ""),
        ...(item?.Items || []).map((i) => i?.MaterialName || ""),
      ].some((val) => String(val).toLowerCase().includes(lower)),
    );
  }, [quotes, searchTerm]);

  // Handle delete
  const handleConfirmDelete = async () => {
    if (!selectedQuote) {
      return;
    }
    setIsDeleting(true);
    try {
      const updated = await deleteQuote(selectedQuote.Id);
      setQuotes(updated);
      toast.success(`Quotation "${selectedQuote.QuoteNo}" deleted successfully`);
    } catch (err) {
      toast.error(err.message || "Failed to delete quotation");
    } finally {
      setIsDeleting(false);
      setSelectedQuote(null);
    }
  };

  // List rows carry no remarks/attachment, so the details come from the quote itself.
  const openDetails = useCallback(async (row) => {
    try {
      setViewQuote(await getQuoteById(row.Id));
    } catch (err) {
      toast.error(err.message || "Failed to load quotation");
    }
  }, []);

  const addPath = "/procurement/quotations/quotes/add";

  /* ─── Column Definitions ──────────────────────────────────────────────── */
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Quote No",
        field: "QuoteNo",
        flex: 1.1,
        minWidth: 130,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "#056363" }}>{params.value}</span>
        ),
      },
      {
        headerName: "Vendor / Supplier",
        field: "VendorName",
        flex: 1.3,
        minWidth: 160,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 500, color: "#1f2937" }}>{params.value}</span>
        ),
      },
      {
        headerName: "Project / Site",
        field: "ProjectName",
        flex: 1.2,
        minWidth: 140,
      },
      {
        headerName: "Linked Requisition",
        field: "RequisitionNo",
        flex: 1.1,
        minWidth: 130,
        cellRenderer: (params) =>
          params.value ? (
            <span
              className="badge"
              style={{
                backgroundColor: "#e0f2fe",
                color: "#0369a1",
                fontSize: "12px",
                padding: "4px 8px",
              }}
            >
              {params.value}
            </span>
          ) : (
            "—"
          ),
      },
      {
        headerName: "Quote Date",
        field: "QuoteDate",
        flex: 1,
        minWidth: 110,
      },
      {
        headerName: "Valid Till",
        field: "ValidTill",
        flex: 1,
        minWidth: 110,
      },
      {
        headerName: "Payment Terms",
        field: "PaymentTerms",
        flex: 1.1,
        minWidth: 130,
      },
      {
        headerName: "Items",
        colId: "itemsCount",
        width: 90,
        valueGetter: (params) => params.data?.Items?.length || 0,
        cellRenderer: (params) => (
          <span className="badge badge-light border px-2 py-1" style={{ fontSize: "12px" }}>
            {params.value} {params.value === 1 ? "item" : "items"}
          </span>
        ),
      },
      {
        headerName: "Grand Total (PKR)",
        field: "GrandTotal",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (params) => (
          <span style={{ fontWeight: 600, color: "#111827" }}>
            {formatAmountWithCommas(params.value || 0)}
          </span>
        ),
      },
      {
        headerName: "Status",
        field: "Status",
        flex: 1,
        minWidth: 120,
        cellRenderer: (params) => {
          const status = params.value || "Received";
          return <StatusChip status={status} type={quoteChipType(status)} />;
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        width: 130,
        pinned: "right",
        lockPosition: "right",
        filter: false,
        sortable: false,
        suppressMenu: true,
        headerComponent: () => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <ActionButton text="New+" newBtn="true" onClick={() => history.push(addPath)} />
          </div>
        ),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              gap: "12px",
            }}
          >
            <FiEye
              title="View Details"
              size="1.15em"
              style={{ cursor: "pointer", color: "#0284c7" }}
              onClick={(e) => {
                e.stopPropagation();
                openDetails(params.data);
              }}
            />
            {/* Edit/Delete are refused by the backend once the quote is in a comparison */}
            {!params.data?.InComparison && (
              <FiEdit2
                title="Edit"
                size="1.1em"
                style={{ cursor: "pointer", color: "#4e545a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  history.push(addPath, { Id: params.data.Id, isEdit: true });
                }}
              />
            )}
            {!params.data?.InComparison && (
              <MdDeleteForever
                title="Delete"
                size="1.2em"
                className="cur_sor"
                style={{ cursor: "pointer", color: "#c0392b" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedQuote(params.data);
                }}
              />
            )}
          </div>
        ),
      },
    ],
    [history, addPath, openDetails],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) =>
        p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value,
      minWidth: 90,
    }),
    [],
  );

  /* ─── Column show / hide ──────────────────────────────────────────────── */
  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  /* ─── Render ──────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Toolbar */}
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Quotes"
        >
          Quotes
        </div>
        <SearchFilter
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search Quotes..."
        />

        <ColumnVisibilityControl
          gridApi={gridRef.current?.api}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
        />
      </div>

      {/* Grid */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-3" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredQuotes || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(e) => {
                if (!e.event.target.closest("button, svg")) {
                  openDetails(e.data);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={!!selectedQuote}
        onClose={() => setSelectedQuote(null)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to delete Quotation "${selectedQuote?.QuoteNo}"?`}
        heading="Delete Quotation"
      />

      {/* View Details Modal */}
      <QuoteDetailsModal open={!!viewQuote} onClose={() => setViewQuote(null)} quote={viewQuote} />
    </>
  );
};

export { QuotesTable };
export default QuotesTable;
